import axios from 'axios';

// Define where your token is stored in localStorage
const AUTH_TOKEN_KEY = 'AUTH_TOKEN_KEY'; // You can name this anything, e.g., 'jwtToken', 'accessToken'

// const API_BASE_URL = 'https://backend-code-jcri.onrender.com';
// or for local development:
// const API_BASE_URL = 'http://localhost:3000';
const API_BASE_URL = 'https://api.japaking.online';

const api = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
        // DO NOT hardcode the token here. It will be added by the interceptor.
    },
});

const isFormData = (value) =>
    typeof FormData !== 'undefined' && value instanceof FormData;

const removeHeader = (headers, headerName) => {
    if (!headers) return;

    if (typeof headers.delete === 'function') {
        headers.delete(headerName);
        return;
    }

    delete headers[headerName];
};

const collegeListCache = new Map();
const collegeListInFlight = new Map();

const buildRequestKey = (endpoint, params = {}) => {
    const normalizedParams = params && Object.keys(params).length ? params : {};
    return `${endpoint}|${JSON.stringify(normalizedParams)}`;
};

const isCollegeEndpoint = (endpoint) => endpoint === '/api/college' || endpoint.startsWith('/api/college/');

const invalidateCollegeListCache = () => {
    collegeListCache.clear();
    collegeListInFlight.clear();
};

// --- Axios Request Interceptor ---
// This interceptor will run before every request made by this 'api' instance.
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem(AUTH_TOKEN_KEY);

        if (token) {

            config.headers['Authorization'] = `Bearer ${token}`;
        }

        if (isFormData(config.data)) {
            removeHeader(config.headers, 'Content-Type');
            removeHeader(config.headers, 'content-type');
        }


        console.log('Authorization Header:', config.headers['Authorization']);

        return config;
    },
    (error) => Promise.reject(error)
);

// --- Axios Response Interceptor (Optional but Recommended) ---
// This can be used for global error handling, refreshing tokens, etc.
api.interceptors.response.use(
    (response) => response, // Just return the response if successful
    async (error) => {
        // console.error('API request failed:', error);
        // Example: Handle 401 Unauthorized errors (token expired, invalid)
        if (error.response && error.response.status === 401) {
            console.error('Unauthorized access - token might be expired or invalid.');
            // You might want to redirect to a login page here
            // window.location.href = '/login'; // Example redirection
            // Or dispatch a logout action in a Redux/Context system
        }
        return Promise.reject(error); // Re-throw the error for component-level handling
    }
);


/**
 * Centralized API service for all modules.
 * Provides a single point of interaction with the backend API.
 */
const apiService = {
    /**
     * Fetches data from a specified endpoint.
     * @param {string} endpoint - The API endpoint to fetch from.
     * @param {object} params - Optional query parameters.
     * @returns {Promise<object>} - A promise that resolves to the fetched data.
     */
    get: async (endpoint, params = {}) => {
        try {
            if (isCollegeEndpoint(endpoint)) {
                const requestKey = buildRequestKey(endpoint, params);

                if (collegeListCache.has(requestKey)) {
                    return collegeListCache.get(requestKey);
                }

                if (collegeListInFlight.has(requestKey)) {
                    return collegeListInFlight.get(requestKey);
                }

                const requestPromise = api.get(endpoint, { params }).then((response) => {
                    collegeListCache.set(requestKey, response.data);
                    return response.data;
                }).finally(() => {
                    collegeListInFlight.delete(requestKey);
                });

                collegeListInFlight.set(requestKey, requestPromise);
                return requestPromise;
            }

            const response = await api.get(endpoint, { params });
            return response.data;
        } catch (error) {
            console.error(`API GET request to ${endpoint} failed:`, error);
            throw error; // Rethrow the error for the caller to handle
        }
    },

    /**
     * Sends a POST request to a specified endpoint.
     * @param {string} endpoint - The API endpoint to send the POST request to.
     * @param {object} data - The data to send in the request body.
     * @returns {Promise<object>} - A promise that resolves to the response data.
     */
    post: async (endpoint, data, config = {}) => {
        try {
            if (isFormData(data)) {
                const headers = { ...(config.headers || {}) };
                removeHeader(headers, 'Content-Type');
                removeHeader(headers, 'content-type');
                config = { ...config, headers };
            }

            const response = await api.post(endpoint, data, config);
            if (isCollegeEndpoint(endpoint)) {
                invalidateCollegeListCache();
            }
            return response.data;
        } catch (error) {
            console.error(`API POST request to ${endpoint} failed:`, error);
            throw error;
        }
    },

    /**
     * Sends a PUT request to a specified endpoint.
     * @param {string} endpoint - The API endpoint to send the PUT request to.
     * @param {object} data - The data to send in the request body.
     * @returns {Promise<object>} - A promise that resolves to the response data.
     */
    put: async (endpoint, data) => {
        try {
            const response = await api.put(endpoint, data);
            if (isCollegeEndpoint(endpoint)) {
                invalidateCollegeListCache();
            }
            return response.data;
        } catch (error) {
            console.error(`API PUT request to ${endpoint} failed:`, error);
            throw error;
        }
    },
    patch: async (endpoint, data) => {
        try {
            const response = await api.patch(endpoint, data);
            if (isCollegeEndpoint(endpoint)) {
                invalidateCollegeListCache();
            }
            return response.data;
        } catch (error) {
            console.error(`API PUT request to ${endpoint} failed:`, error);
            throw error;
        }
    },
    /**
     * Sends a DELETE request to a specified endpoint.
     * @param {string} endpoint - The API endpoint to send the DELETE request to.
     * @param {object} config - Optional configuration (e.g. data body).
     * @returns {Promise<object>} - A promise that resolves to the response data.
     */
    delete: async (endpoint, config = {}) => {
        try {
            const response = await api.delete(endpoint, config);
            if (isCollegeEndpoint(endpoint)) {
                invalidateCollegeListCache();
            }
            return response.data;
        } catch (error) {
            console.error(`API DELETE request to ${endpoint} failed:`, error);
            throw error;
        }
    },

    // Add more methods for other HTTP methods as needed (PATCH, etc.)
};

export default apiService;
